/**
 * @fileoverview AI Move Executor
 *
 * Extracts the critical piece lookup + execution pattern from BotGameController.
 * This replicates the exact code path where production Firebase+AI bugs live:
 * - Piece lookup by type+side (not ID, since IDs regenerate on FEN decode)
 * - toPiece re-lookup from current internal state
 * - sideEffect piece re-mapping to current references
 *
 * Used by both AITestRunner (local) and AIFirebaseTestRunner (Firebase).
 */

import { ZodiacEngine } from "../zodiacEngine/zodiacEngine";
import { GameState, IPiece } from "../zodiacEngine/types";
import { AIMoveResult } from "../zodiacEngine/AI/Core/IAIPlayer";

export interface AIMoveExecutionResult {
  success: boolean;
  error?: string;
  moveDescription: string;
}

/**
 * Execute an AI-generated move on the engine, replicating BotGameController's
 * piece lookup pattern (BotGameController.ts lines 241-294).
 *
 * This function handles:
 * 1. Looking up pieces from engine's CURRENT internal state (not stale gameState)
 * 2. Matching by type+side (not ID, which changes on Firebase round-trip)
 * 3. Re-mapping sideEffect piece references to current internal pieces
 */
export async function executeAIMove(
  engine: ZodiacEngine,
  moveResult: AIMoveResult,
  gameState: GameState
): Promise<AIMoveExecutionResult> {
  try {
    // IMPORTANT: Look up the piece from the engine's CURRENT internal state,
    // not the gameState which may be stale after Firebase round-trips.
    // removePiece() uses reference comparison (x !== piece), so the piece
    // must be the exact object from the current internal board.
    const currentGameState = (engine as any).gameStateManager.getGameState();
    const fromSquare =
      currentGameState.board[moveResult.fromPosition.row]?.[
        moveResult.fromPosition.col
      ];

    // Find the AI's piece from the gameState snapshot (may have stale refs)
    const aiPiece = gameState.board[moveResult.fromPosition.row]?.[
      moveResult.fromPosition.col
    ]?.Pieces?.find((p: IPiece) => p.id === moveResult.move.pieceId);

    if (!aiPiece) {
      return {
        success: false,
        error: `AI referenced a piece (ID: ${moveResult.move.pieceId}) that does not exist at (${moveResult.fromPosition.row},${moveResult.fromPosition.col})`,
        moveDescription: "",
      };
    }

    // Find the matching piece in current internal state by type+side
    const piece = fromSquare?.Pieces?.find(
      (p: IPiece) => p.type === aiPiece.type && p.isLight === aiPiece.isLight
    );

    if (!piece) {
      return {
        success: false,
        error: `Could not find ${aiPiece.isLight ? "Light" : "Dark"} ${aiPiece.type} at source position (${moveResult.fromPosition.row},${moveResult.fromPosition.col}) in current state`,
        moveDescription: "",
      };
    }

    // Also re-lookup toPiece from current internal state if it exists
    let toPiece = moveResult.move.toPiece;
    if (toPiece) {
      const toSquare =
        currentGameState.board[moveResult.move.position.row]?.[
          moveResult.move.position.col
        ];
      const currentToPiece = toSquare?.Pieces?.find(
        (p: IPiece) => p.type === toPiece!.type && p.isLight === toPiece!.isLight
      );
      toPiece = currentToPiece || toPiece;
    }

    // Re-map sideEffects to reference current internal pieces
    const sideEffects = moveResult.move.sideEffects.map((se: any) => {
      if (!se.piecesToEffect || se.piecesToEffect.length === 0) return se;
      const updatedPieces = se.piecesToEffect.map((effectPiece: IPiece) => {
        const seSquare =
          currentGameState.board[se.position.row]?.[se.position.col];
        const currentPiece = seSquare?.Pieces?.find(
          (p: IPiece) =>
            p.type === effectPiece.type && p.isLight === effectPiece.isLight
        );
        return currentPiece || effectPiece;
      });
      return { ...se, piecesToEffect: updatedPieces };
    });

    // Build move description
    const color = piece.isLight ? "Light" : "Dark";
    const moveDescription =
      `${color} ${piece.type} (${moveResult.fromPosition.row},${moveResult.fromPosition.col}) -> ` +
      `(${moveResult.move.position.row},${moveResult.move.position.col})`;

    // Execute the move
    await engine.makeMove(
      moveResult.fromPosition,
      moveResult.move.position,
      piece,
      toPiece,
      moveResult.move.moveType,
      sideEffects
    );

    return {
      success: true,
      moveDescription,
    };
  } catch (error) {
    return {
      success: false,
      error: `Move execution failed: ${error instanceof Error ? error.message : String(error)}`,
      moveDescription: "",
    };
  }
}
